import { SYNC_FREQUENCY_LABEL } from './format';
import type { DeviceFamily, IntegrationProvider, SyncFrequency } from './types';

export const PROVIDER_LABEL: Record<IntegrationProvider, string> = {
  BLACKBAUD: 'Blackbaud',
  GOOGLE: 'Google',
  JAMF_PRO: 'Jamf Pro',
  JAMF_SCHOOL: 'Jamf School',
  MOSYLE: 'Mosyle',
  ARUBA_CENTRAL: 'HPE Aruba New Central',
  INTUNE: 'Microsoft Intune',
  APPLE_SCHOOL_MANAGER: 'Apple School Manager',
  EMAIL: 'Help desk email',
  FACILITIES_EMAIL: 'Facilities email',
  GOOGLE_PROVISIONING: 'Google Workspace Provisioning',
  ONEROSTER: 'OneRoster',
  BLACKBAUD_BILLING: 'Blackbaud Billing Management',
};

export const PROVIDER_TITLE: Record<IntegrationProvider, string> = {
  BLACKBAUD: 'Blackbaud SKY API',
  GOOGLE: 'Google Workspace',
  JAMF_PRO: 'Jamf Pro',
  JAMF_SCHOOL: 'Jamf School',
  MOSYLE: 'Mosyle',
  ARUBA_CENTRAL: 'HPE Aruba New Central',
  INTUNE: 'Microsoft Intune',
  APPLE_SCHOOL_MANAGER: 'Apple School Manager',
  EMAIL: 'Help desk email',
  FACILITIES_EMAIL: 'Facilities email',
  GOOGLE_PROVISIONING: 'Google Workspace Provisioning',
  ONEROSTER: 'SIS rostering (OneRoster)',
  BLACKBAUD_BILLING: 'Blackbaud Billing Management',
};

/** URL segment for an integration's API routes, e.g. "apple-school-manager". */
export const providerPath = (provider: IntegrationProvider) => provider.toLowerCase().replace(/_/g, '-');

/** Blackbaud Billing Management posts invoices when asked, over Blackbaud's connection; it has nothing to sync. */
export const syncs = (provider: IntegrationProvider) => provider !== 'BLACKBAUD_BILLING';

/** The help desk and facilities mailboxes. */
export const isMailbox = (provider: IntegrationProvider): provider is 'EMAIL' | 'FACILITIES_EMAIL' => provider === 'EMAIL' || provider === 'FACILITIES_EMAIL';

const MINUTE_FREQUENCIES: SyncFrequency[] = ['EVERY_MINUTE', 'EVERY_5_MINUTES', 'EVERY_15_MINUTES'];

/** Schedules offered for an integration. Only mailboxes are checked every few minutes. */
export function frequencyOptions(provider: IntegrationProvider, current?: SyncFrequency): SyncFrequency[] {
  const all = Object.keys(SYNC_FREQUENCY_LABEL) as SyncFrequency[];
  const options =
    isMailbox(provider)
      ? all.filter((f) => f === 'MANUAL' || f === 'HOURLY' || MINUTE_FREQUENCIES.includes(f))
      : all.filter((f) => !MINUTE_FREQUENCIES.includes(f));
  return current && !options.includes(current) ? [...options, current] : options;
}

/** Times offered for daily syncs, every half hour, plus the current time when it falls between them. */
export function syncTimeOptions(current?: string): string[] {
  const times = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
  return current && !times.includes(current) ? [...times, current].sort() : times;
}

/** "02:00" → "2:00 AM". */
export function formatTimeOfDay(time: string) {
  const [hour, minute] = time.split(':').map(Number);
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(Date.UTC(2000, 0, 1, hour, minute));
}

/** A time zone as people say it ("Eastern Time"), or its IANA name when the browser can't name it. */
export function timeZoneName(timezone: string) {
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return name && !name.startsWith('GMT') ? name : timezone;
  } catch {
    return timezone;
  }
}

export const DEVICE_FAMILY_LABEL: Record<DeviceFamily, string> = {
  MAC: 'Mac',
  IPAD: 'iPad',
  IPHONE: 'iPhone',
  APPLE_TV: 'Apple TV',
  WATCH: 'Apple Watch',
  VISION: 'Apple Vision Pro',
  WINDOWS: 'Windows PC',
  ANDROID: 'Android',
  LINUX: 'Linux',
  OTHER: 'Other',
};

export function formatStorage(mb: number | null | undefined) {
  if (mb === null || mb === undefined) return null;
  return mb >= 1024 ? `${Math.round((mb / 1024) * 10) / 10} GB` : `${mb} MB`;
}
